import * as React from 'react';
import cx from 'classnames';
import RouterLink from './RouterLink';
import { addClassName } from '../utils/classes';
import { getBaseUrl } from '../utils/globals';
import useCategoryFragment from '../hooks/useCategoryFragment';
import useIsCategoryActive from '../hooks/useIsCategoryActive';
import useSubCategories from '../hooks/useSubCategories';
import { Category } from './categoryFetch';
import { CategoryContext } from '../contexts';
import { TotalCount } from './articles';
export { TotalCount as ArticleCount, Category as Fetch };
/**
 * Render the title of a category, if you don't pass in a categoryId
 * then it will use the current category.
 * This will fetch the category if we don't have it already.
 */
export const Title = ({ categoryId }) => {
    const category = useCategoryFragment(categoryId);
    return React.createElement(React.Fragment, null, category && category.title);
};
Title.displayName = 'Category.Title';
/**
 * Render the featured Image Url of a category, if you don't pass in a categoryId
 * then it will use the current category.
 * This will fetch the category if we don't have it already.
 */
export const FeaturedImageUrl = ({ className, categoryId, }) => {
    const category = useCategoryFragment(categoryId);
    return !category ? null : (React.createElement("img", { src: category.featuredImageUrl || '', className: cx(className, addClassName('featured-image')) }));
};
FeaturedImageUrl.displayName = 'Category.FeaturedImageUrl';
/**
 * This will render an A tag with a href that points to a category page.
 * If you pass in a categoryId then it will use that otherwise it will
 * use the current category context.
 */
export const Link = ({ children, categoryId, slug, className, 'data-testid': dataTestId, ...props }) => {
    const baseUrl = getBaseUrl();
    const category = useCategoryFragment(categoryId);
    const _slug = slug ? slug : category ? category.slug : categoryId;
    if (!_slug)
        return null;
    return (React.createElement(RouterLink, Object.assign({}, props, { className: cx(className, addClassName('category-link')), to: `${baseUrl}/categories/${_slug}`, "data-testid": dataTestId }), children));
};
Link.displayName = 'Category.Link';
/**
 * Renders out the child content if the category either from the categoryId prop or if not provided the
 * current category is active
 * If a render prop is passed as the child then a boolean will be provided with the is active state
 * NOTE: see useIsCategoryActive for a definition of what is an active category
 */
export const IsActive = ({ children, categoryId }) => {
    const isActive = useIsCategoryActive(categoryId);
    if (typeof children === 'function')
        return React.createElement(React.Fragment, null, children(isActive));
    if (!isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsActive.displayName = 'Category.IsActive';
/**
 * Renders out the child content if the category either from the categoryId prop or if not provided the
 * current category is NOT active
 * If a render prop is passed as the child then a boolean will be provided with the is non-active state
 * NOTE: see useIsCategoryActive for a definition of what is an active category
 */
export const IsNotActive = ({ children, categoryId }) => {
    const isActive = useIsCategoryActive(categoryId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(!isActive));
    }
    if (isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsNotActive.displayName = 'Category.IsNotActive';
export const isCategoryLoopFunction = (arg) => {
    return typeof arg === 'function';
};
/** Checks the type of the react child and returns in the correct way */
export function returnChild(children, { category, index }) {
    return React.isValidElement(children)
        ? children
        : isCategoryLoopFunction(children)
            ? children(category, index)
            : null;
}
/**
 * For each of the sub categories of the either the current category in context or the categoryId passed in
 * it repeats the children passed in.
 * So if you pass a render prop as a child it will be called with each sub category result.
 * Else if you pass in React Elements they will be repeated for each sub category result.
 */
export const LoopSubCategories = ({ children, }) => {
    const subCategories = useSubCategories();
    return (React.createElement(React.Fragment, null, (subCategories || []).map((sub, index) => (React.createElement(CategoryContext.Provider, { key: sub.id, value: sub }, returnChild(children, {
        category: sub,
        index,
    }))))));
};
LoopSubCategories.displayName = 'Category.LoopSubCategories';
//# sourceMappingURL=category.js.map